import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Slider,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { getMPRInfo, getMPRStatus, getMPRUrl, getMIPUrl, getMPRFusionUrl, warmMPRFusion } from '../api';
import { consumeMprWheelSteps, MPR_WHEEL_COALESCING_MARKER, normalizeMprWheelDelta, shouldScheduleMprWheelFrame } from './mprWheel';

const INTERACTIVE_FUSION_WARMUP_MAX_FRAMES = 24;

const PLANE_LABELS = { axial: 'AXIAL', coronal: 'CORONAL', sagittal: 'SAGITTAL' };

// Map UI label → backend projection param
const PROJECTION_MAP = { MIP: 'mip', MinIP: 'minip', Avg: 'average' };

const Crosshair = ({ hPct, vPct }) => (
  <>
    {/* Horizontal line */}
    <div style={{
      position: 'absolute',
      left: 0, right: 0,
      top: `${hPct * 100}%`,
      height: 1,
      background: 'red',
      pointerEvents: 'none',
      opacity: 0.8,
    }} />
    {/* Vertical line */}
    <div style={{
      position: 'absolute',
      top: 0, bottom: 0,
      left: `${vPct * 100}%`,
      width: 1,
      background: 'red',
      pointerEvents: 'none',
      opacity: 0.8,
    }} />
  </>
);

const MPRPanel = ({
  plane, label, imgUrl,
  sliceIdx, maxSlice, onSliceChange,
  hPct, vPct, onPanelClick,
  projection, slabThickness,
  onProjectionChange, onSlabChange, preparing = false,
}) => {
  const panelRef = useRef(null);
  const wheelPixelsRef = useRef(0);
  const wheelFrameRef = useRef(null);

  const flushWheelFrame = useCallback(() => {
    wheelFrameRef.current = null;
    const consumed = consumeMprWheelSteps(wheelPixelsRef.current);
    wheelPixelsRef.current = consumed.remainder;
    if (consumed.steps !== 0) {
      onSliceChange(prev => Math.max(0, Math.min(maxSlice, prev + consumed.steps)));
    }
    if (shouldScheduleMprWheelFrame(wheelPixelsRef.current)) {
      wheelFrameRef.current = requestAnimationFrame(flushWheelFrame);
    }
  }, [maxSlice, onSliceChange]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    wheelPixelsRef.current += normalizeMprWheelDelta(e);
    if (wheelFrameRef.current === null) {
      wheelFrameRef.current = requestAnimationFrame(flushWheelFrame);
    }
  }, [flushWheelFrame]);

  useEffect(() => {
    const el = panelRef.current;
    if (el) el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      if (el) el.removeEventListener('wheel', handleWheel);
      if (wheelFrameRef.current !== null) {
        cancelAnimationFrame(wheelFrameRef.current);
        wheelFrameRef.current = null;
      }
    };
  }, [handleWheel]);

  const handleClick = useCallback((e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const colFrac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const rowFrac = Math.max(0, Math.min(1, (e.clientY - rect.top)  / rect.height));
    onPanelClick(colFrac, rowFrac);
  }, [onPanelClick]);

  return (
    <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#111',
      border: '1px solid #1e1e1e',
      overflow: 'hidden',
      minWidth: 0,
    }} data-mpr-wheel-coalescing={MPR_WHEEL_COALESCING_MARKER}>
      {/* Image area */}
      <Box
        ref={panelRef}
        onClick={handleClick}
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: 'crosshair',
          bgcolor: '#0a0a0a',
        }}
      >
        {/* Label overlay */}
        <Typography sx={{
          position: 'absolute', top: 6, left: 8,
          color: '#fff', fontSize: 10, fontWeight: 'bold',
          letterSpacing: 2, zIndex: 5, pointerEvents: 'none',
          textShadow: '0 0 6px #000',
        }}>
          {label}
        </Typography>

        {/* Projection badge (top-right) */}
        {projection !== 'MPR' && (
          <Typography sx={{
            position: 'absolute', top: 6, right: 8,
            color: '#00bcd4', fontSize: 9, fontWeight: 'bold',
            letterSpacing: 1.5, zIndex: 5, pointerEvents: 'none',
            textShadow: '0 0 6px #000',
          }}>
            {projection} ×{slabThickness}
          </Typography>
        )}

        {imgUrl ? (
          <img
            src={imgUrl}
            alt={plane}
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : preparing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#00bcd4', fontFamily: 'monospace', letterSpacing: 1.5 }}>
            <CircularProgress size={22} thickness={2} sx={{ color: '#00bcd4' }} />
            <Typography sx={{ color: '#00bcd4', fontSize: 10, fontWeight: 800 }}>PREPARING MPR…</Typography>
          </Box>
        ) : null}

        {/* Crosshair lines */}
        <Crosshair hPct={hPct} vPct={vPct} />
      </Box>

      {/* Slice slider */}
      <Box sx={{ px: 1.5, pb: 0.25, pt: 0.25, bgcolor: '#080808' }}>
        <Typography sx={{ color: '#444', fontSize: 8, mb: 0.25 }}>
          {sliceIdx + 1} / {maxSlice + 1}
        </Typography>
        <Slider
          value={sliceIdx}
          min={0}
          max={maxSlice}
          step={1}
          onChange={(_, v) => onSliceChange(() => v)}
          size="small"
          sx={{
            color: '#00bcd4', height: 2, padding: '4px 0',
            '& .MuiSlider-thumb': { width: 8, height: 8, '&:hover': { boxShadow: '0 0 0 6px rgba(0,188,212,0.2)' } },
            '& .MuiSlider-rail': { bgcolor: '#222' },
          }}
        />
      </Box>

      {/* Projection toolbar */}
      <Box sx={{
        px: 1, pb: 0.75, pt: 0.25, bgcolor: '#080808',
        borderTop: '1px solid #111',
        display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        {/* Mode selector */}
        <ToggleButtonGroup
          value={projection}
          exclusive
          onChange={(_, val) => { if (val !== null) onProjectionChange(val); }}
          size="small"
          sx={{
            height: 20,
            '& .MuiToggleButton-root': {
              px: 0.75, py: 0,
              fontSize: 8,
              fontWeight: 'bold',
              letterSpacing: 0.5,
              color: '#555',
              borderColor: '#222',
              lineHeight: '20px',
              '&.Mui-selected': {
                color: '#00bcd4',
                bgcolor: 'rgba(0,188,212,0.12)',
                borderColor: '#00bcd4',
              },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
            },
          }}
        >
          <ToggleButton value="MPR">MPR</ToggleButton>
          <ToggleButton value="MIP">MIP</ToggleButton>
          <ToggleButton value="MinIP">MinIP</ToggleButton>
          <ToggleButton value="Avg">Avg</ToggleButton>
        </ToggleButtonGroup>

        {/* Slab thickness — only visible when not in MPR mode */}
        {projection !== 'MPR' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#555', fontSize: 8, whiteSpace: 'nowrap', minWidth: 28 }}>
              slab {slabThickness}
            </Typography>
            <Slider
              value={slabThickness}
              min={1}
              max={50}
              step={1}
              onChange={(_, v) => onSlabChange(v)}
              size="small"
              sx={{
                color: '#00bcd4', height: 2, padding: '3px 0',
                '& .MuiSlider-thumb': { width: 7, height: 7 },
                '& .MuiSlider-rail': { bgcolor: '#222' },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const MPRView = ({ studyId, seriesId, ww, wc, fusionPetSeriesId, fusionOpacity = 0.45, fusionColormap = 'hot', fusionPetSlabMm = 1 }) => {
  const [volumeInfo, setVolumeInfo] = useState(null);
  const [axialSlice,    setAxialSlice]    = useState(0);
  const [coronalSlice,  setCoronalSlice]  = useState(0);
  const [sagittalSlice, setSagittalSlice] = useState(0);
  const [mprAsyncStatus, setMprAsyncStatus] = useState(null);

  // Projection mode per plane: 'MPR' | 'MIP' | 'MinIP' | 'Avg'
  const [projections, setProjections] = useState({
    axial: 'MPR', coronal: 'MPR', sagittal: 'MPR',
  });
  // Slab thickness per plane (slices)
  const [slabThickness, setSlabThickness] = useState({
    axial: 10, coronal: 10, sagittal: 10,
  });

  const pollRef = useRef(null);
  const fusionWarmKeyRef = useRef(null);

  // Clear previously rendered CT/PET fusion state as soon as the study, CT base, or
  // PET overlay changes. Without this, route/navigation or manual series changes can
  // keep showing old MPR image URLs until the next info poll resolves, which looks like
  // stale cache or wrong CT/PT pairing.
  useEffect(() => {
    setVolumeInfo(null);
    setAxialSlice(0);
    setCoronalSlice(0);
    setSagittalSlice(0);
    fusionWarmKeyRef.current = null;
    setMprAsyncStatus(null);
  }, [studyId, seriesId, fusionPetSeriesId]);

  // Poll mpr/info until volume_ready
  useEffect(() => {
    if (!studyId || !seriesId) return;

    const poll = () => {
      getMPRInfo(studyId, seriesId)
        .then(info => {
          if (info.volume_ready || info.mpr_async_required) {
            setVolumeInfo(info);
            if (info.asyncStatus) setMprAsyncStatus(info.asyncStatus.status || info.asyncStatus);
            const nextAxial = Math.max(0, Math.floor((info.n_axial || 1) / 2));
            const nextCoronal = Math.max(0, Math.floor((info.n_coronal || 1) / 2));
            const nextSagittal = Math.max(0, Math.floor((info.n_sagittal || 1) / 2));
            setAxialSlice(nextAxial);
            setCoronalSlice(nextCoronal);
            setSagittalSlice(nextSagittal);
            if (info.mpr_async_required) {
              clearInterval(pollRef.current);
              return;
            }
            if (fusionPetSeriesId) {
              const warmKey = `${studyId}|${seriesId}|${fusionPetSeriesId}|${Math.round(ww)}|${Math.round(wc)}|${fusionOpacity}|${fusionColormap}|slab${fusionPetSlabMm}`;
              if (fusionWarmKeyRef.current !== warmKey) {
                fusionWarmKeyRef.current = warmKey;
                [
                  { planes: 'axial', center: nextAxial },
                  { planes: 'coronal', center: nextCoronal },
                  { planes: 'sagittal', center: nextSagittal },
                ].forEach(({ planes, center }) => {
                  warmMPRFusion(studyId, seriesId, fusionPetSeriesId, {
                    planes,
                    ww,
                    wc,
                    opacity: fusionOpacity,
                    colormap: fusionColormap,
                    petSlabMm: fusionPetSlabMm,
                    center,
                    maxFrames: INTERACTIVE_FUSION_WARMUP_MAX_FRAMES,
                  }).catch(err => console.error('MPR fusion warmup error:', err));
                });
              }
            }
            clearInterval(pollRef.current);
          }
        })
        .catch(err => console.error('MPR info poll error:', err));
    };

    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [studyId, seriesId, fusionPetSeriesId, ww, wc, fusionOpacity, fusionColormap, fusionPetSlabMm]);

  useEffect(() => {
    if (!studyId || !seriesId || !volumeInfo?.mpr_async_required || fusionPetSeriesId) return undefined;
    let cancelled = false;
    const pollStatus = () => {
      Promise.all([
        getMPRStatus(studyId, seriesId, { plane: 'axial', slice: axialSlice, ww, wc, enqueue: true }),
        getMPRStatus(studyId, seriesId, { plane: 'coronal', slice: coronalSlice, ww, wc, enqueue: true }),
        getMPRStatus(studyId, seriesId, { plane: 'sagittal', slice: sagittalSlice, ww, wc, enqueue: true }),
      ]).then(([axial, coronal, sagittal]) => {
        if (cancelled) return;
        setMprAsyncStatus({
          axial: axial.status?.axial,
          coronal: coronal.status?.coronal,
          sagittal: sagittal.status?.sagittal,
        });
      }).catch(err => console.error('MPR async status poll error:', err));
    };
    pollStatus();
    const id = setInterval(pollStatus, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [studyId, seriesId, volumeInfo?.mpr_async_required, fusionPetSeriesId, axialSlice, coronalSlice, sagittalSlice, ww, wc]);

  if (!volumeInfo) {
    return (
      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 2, bgcolor: '#0d0d0d',
      }}>
        <CircularProgress size={28} thickness={2} sx={{ color: '#00bcd4' }} />
        <Typography sx={{ color: '#555', fontSize: 11, letterSpacing: 2 }}>
          BUILDING VOLUME…
        </Typography>
      </Box>
    );
  }

  const { n_axial, n_coronal, n_sagittal } = volumeInfo;

  // Crosshair fractions
  const axialH   = n_coronal  > 0 ? coronalSlice  / n_coronal  : 0.5;
  const axialV   = n_sagittal > 0 ? sagittalSlice / n_sagittal : 0.5;
  const coronalH = n_axial    > 0 ? 1 - axialSlice / n_axial   : 0.5;
  const coronalV = n_sagittal > 0 ? sagittalSlice / n_sagittal : 0.5;
  const sagittalH = n_axial   > 0 ? 1 - axialSlice / n_axial   : 0.5;
  const sagittalV = n_coronal > 0 ? coronalSlice  / n_coronal  : 0.5;

  // Click handlers (unchanged)
  const handleAxialClick = (colFrac, rowFrac) => {
    setSagittalSlice(Math.round(colFrac * (n_sagittal - 1)));
    setCoronalSlice( Math.round(rowFrac * (n_coronal  - 1)));
  };
  const handleCoronalClick = (colFrac, rowFrac) => {
    setSagittalSlice(Math.round(colFrac  * (n_sagittal - 1)));
    setAxialSlice(   Math.round((1 - rowFrac) * (n_axial - 1)));
  };
  const handleSagittalClick = (colFrac, rowFrac) => {
    setCoronalSlice(Math.round(colFrac  * (n_coronal - 1)));
    setAxialSlice(  Math.round((1 - rowFrac) * (n_axial - 1)));
  };

  // Helpers to update per-plane state
  const setProjection = (plane, val) =>
    setProjections(prev => ({ ...prev, [plane]: val }));
  const setSlab = (plane, val) =>
    setSlabThickness(prev => ({ ...prev, [plane]: val }));

  // URL builders — use /mip endpoint when projection is not MPR
  const buildUrl = (plane, sliceIdx) => {
    const proj = projections[plane];
    const slab = slabThickness[plane];
    if (proj === 'MPR') {
      if (fusionPetSeriesId) {
        return getMPRFusionUrl(studyId, seriesId, fusionPetSeriesId, plane, sliceIdx, ww, wc, fusionOpacity, fusionColormap, fusionPetSlabMm);
      }
      if (volumeInfo?.mpr_async_required && mprAsyncStatus?.[plane]?.state !== 'ready') {
        return '';
      }
      return getMPRUrl(studyId, seriesId, plane, sliceIdx, ww, wc);
    }
    return getMIPUrl(studyId, seriesId, plane, sliceIdx, slab, PROJECTION_MAP[proj], ww, wc);
  };

  const axialUrl    = buildUrl('axial',    axialSlice);
  const coronalUrl  = buildUrl('coronal',  coronalSlice);
  const sagittalUrl = buildUrl('sagittal', sagittalSlice);

  return (
    <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      gap: '2px',
      bgcolor: '#050505',
      overflow: 'hidden',
      height: '100%',
    }}>
      <MPRPanel
        plane="axial"
        label={PLANE_LABELS.axial}
        imgUrl={axialUrl}
        sliceIdx={axialSlice}
        maxSlice={Math.max(0, n_axial - 1)}
        onSliceChange={setAxialSlice}
        hPct={axialH}
        vPct={axialV}
        onPanelClick={handleAxialClick}
        projection={projections.axial}
        slabThickness={slabThickness.axial}
        onProjectionChange={(val) => setProjection('axial', val)}
        onSlabChange={(val) => setSlab('axial', val)}
        preparing={volumeInfo?.mpr_async_required && projections.axial === 'MPR' && mprAsyncStatus?.axial?.state !== 'ready'}
      />
      <MPRPanel
        plane="coronal"
        label={PLANE_LABELS.coronal}
        imgUrl={coronalUrl}
        sliceIdx={coronalSlice}
        maxSlice={Math.max(0, n_coronal - 1)}
        onSliceChange={setCoronalSlice}
        hPct={coronalH}
        vPct={coronalV}
        onPanelClick={handleCoronalClick}
        projection={projections.coronal}
        slabThickness={slabThickness.coronal}
        onProjectionChange={(val) => setProjection('coronal', val)}
        onSlabChange={(val) => setSlab('coronal', val)}
        preparing={volumeInfo?.mpr_async_required && projections.coronal === 'MPR' && mprAsyncStatus?.coronal?.state !== 'ready'}
      />
      <MPRPanel
        plane="sagittal"
        label={PLANE_LABELS.sagittal}
        imgUrl={sagittalUrl}
        sliceIdx={sagittalSlice}
        maxSlice={Math.max(0, n_sagittal - 1)}
        onSliceChange={setSagittalSlice}
        hPct={sagittalH}
        vPct={sagittalV}
        onPanelClick={handleSagittalClick}
        projection={projections.sagittal}
        slabThickness={slabThickness.sagittal}
        onProjectionChange={(val) => setProjection('sagittal', val)}
        onSlabChange={(val) => setSlab('sagittal', val)}
        preparing={volumeInfo?.mpr_async_required && projections.sagittal === 'MPR' && mprAsyncStatus?.sagittal?.state !== 'ready'}
      />
    </Box>
  );
};

export default MPRView;
